<script lang="ts">
  import { user } from '../lib/stores';
  import Landing from './Landing.svelte';
  import Upload from './Upload.svelte';
  import FileList from './FileList.svelte';
  import Gallery from './Gallery.svelte';

  let { route }: { route: string } = $props();

  let WalletComponent: any = $state(null);
  $effect(() => {
    if (route === '/wallet' && user.loggedIn && !WalletComponent) {
      import('./Wallet.svelte').then((m) => { WalletComponent = m.default; });
    }
  });

  let mediaSha256 = $derived(
    route.startsWith('/media/') ? route.slice('/media/'.length).split('.')[0] : null
  );
</script>

<div class="min-h-screen bg-[#0d1117] text-[#e6edf3]">
  <nav class="border-b border-[#21262d] px-3 py-2 flex flex-wrap items-center justify-between gap-1 max-w-5xl mx-auto">
    <a href="#/" class="text-sm sm:text-lg font-bold text-[#58a6ff] whitespace-nowrap">blossom.psbt.me</a>
    <div class="flex items-center gap-0.5 sm:gap-3 flex-wrap">
      <a href="#/explore" class="px-2 py-1.5 text-xs sm:text-sm rounded-md transition-colors {route === '/explore' ? 'text-white bg-[#21262d]' : 'text-[#8b949e] hover:text-white hover:bg-[#21262d]'}">Explore</a>
      {#if user.loggedIn}
        <a href="#/upload" class="px-2 py-1.5 text-xs sm:text-sm rounded-md transition-colors {route === '/upload' || route === '/' ? 'text-white bg-[#21262d]' : 'text-[#8b949e] hover:text-white hover:bg-[#21262d]'}">Upload</a>
        <a href="#/files" class="px-2 py-1.5 text-xs sm:text-sm rounded-md transition-colors {route === '/files' ? 'text-white bg-[#21262d]' : 'text-[#8b949e] hover:text-white hover:bg-[#21262d]'}">Files</a>
        <a href="#/wallet" class="px-2 py-1.5 text-xs sm:text-sm rounded-md transition-colors {route === '/wallet' ? 'text-white bg-[#21262d]' : 'text-[#8b949e] hover:text-white hover:bg-[#21262d]'}">Wallet</a>
        <span class="text-xs text-[#484f58] ml-1 hidden sm:inline">{user.pubkey?.slice(0, 8)}...</span>
      {/if}
    </div>
  </nav>

  <main class="max-w-5xl mx-auto px-4 py-6">
    {#if mediaSha256}
      <div class="space-y-4">
        <div class="flex items-center justify-between">
          <a href="#/explore" class="text-sm text-[#58a6ff] hover:underline">← Back to Explore</a>
        </div>
        <div class="bg-[#161b22] border border-[#21262d] rounded-lg overflow-hidden max-w-4xl mx-auto">
          <div class="flex items-center justify-between px-4 py-3 border-b border-[#21262d]">
            <div class="flex items-center gap-2 min-w-0">
              <code class="text-xs font-mono text-[#58a6ff] break-all">{mediaSha256}</code>
            </div>
          </div>
          <div class="flex items-center justify-center bg-[#0d1117] min-h-[300px] p-4">
            {#await fetch(`https://blossom.psbt.me/${mediaSha256}`, { method: 'HEAD' }).then(r => r.headers.get('content-type') || 'application/octet-stream')}
              <div class="w-8 h-8 rounded-full border-2 border-[#21262d] border-t-[#58a6ff] animate-spin"></div>
            {:then contentType}
              {#if contentType.startsWith('image/')}
                <img src="https://blossom.psbt.me/{mediaSha256}" alt={mediaSha256.slice(0,12)} class="max-w-full max-h-[70vh] object-contain rounded" />
              {:else if contentType.startsWith('video/')}
                <video src="https://blossom.psbt.me/{mediaSha256}" controls class="max-w-full max-h-[70vh] rounded"></video>
              {:else if contentType.startsWith('audio/')}
                <audio src="https://blossom.psbt.me/{mediaSha256}" controls class="w-full"></audio>
              {:else}
                <div class="text-center py-12">
                  <p class="text-[#8b949e] text-sm mb-4">{contentType}</p>
                  <a href="https://blossom.psbt.me/{mediaSha256}" download class="inline-block px-4 py-2 bg-[#238636] hover:bg-[#2ea043] text-white rounded-md text-xs font-medium transition-colors">
                    Download File
                  </a>
                </div>
              {/if}
            {:catch}
              <p class="text-[#f85149] text-sm">File not found</p>
            {/await}
          </div>
          <div class="px-4 py-3 border-t border-[#21262d] bg-[#0d1117]/50">
            {#await fetch(`https://blossom.psbt.me/${mediaSha256}/metadata`).then(r => r.ok ? r.json() : null).catch(() => null)}
              <span class="text-xs text-[#484f58]">Loading metadata...</span>
            {:then meta}
              {#if meta}
                <div class="flex flex-wrap items-center justify-between gap-3">
                  <div class="flex flex-wrap items-center gap-2 text-xs text-[#484f58]">
                    <span>{(meta.size / (1024*1024)).toFixed(1)} MB</span>
                    <span>·</span>
                    <span>{meta.type}</span>
                    <span>·</span>
                    <span>{meta.downloads} downloads</span>
                    <span>·</span>
                    <span style="color: {meta.expires_at * 1000 - Date.now() < 10 * 86400000 ? '#f85149' : '#484f58'}">
                      Expires {new Date(meta.expires_at * 1000).toLocaleDateString()}
                    </span>
                  </div>
                  {#if !user.loggedIn}
                    <a href="#/" class="text-[10px] text-[#a371f7] hover:underline">Login to extend →</a>
                  {:else}
                    <button
                      class="text-[10px] px-2 py-1 rounded-md bg-[#21262d] hover:bg-[#30363d] text-[#a371f7] transition-colors"
                      onclick={async () => {
                        try {
                          const { payForUpload } = await import('../lib/cashu-payment');
                          const token = await payForUpload(21, (s) => {});
                          const { signEvent } = await import('../lib/auth');
                          const { createUploadAuth, encodeAuthorizationHeader } = await import('blossom-client-sdk');
                          const auth = createUploadAuth((e) => signEvent(e), mediaSha256, { type: 'upload', expiration: Math.floor(Date.now()/1000) + 300 });
                          const resp = await fetch(`https://blossom.psbt.me/${mediaSha256}`, {
                            method: 'PATCH',
                            headers: { 'Authorization': encodeAuthorizationHeader(auth), 'X-Cashu': token },
                          });
                          if (resp.ok) {
                            alert('Storage extended!');
                            window.location.reload();
                          } else {
                            alert('Extend failed: ' + resp.status);
                          }
                        } catch (e) {
                          alert('Payment failed: ' + (e instanceof Error ? e.message : 'unknown'));
                        }
                      }}
                    >
                      ⚡ Extend (+21 sats)
                    </button>
                  {/if}
                </div>
              {/if}
            {/await}
          </div>
        </div>
      </div>
    {:else if route === '/explore'}
      <Gallery />
    {:else if !user.loggedIn}
      <Landing />
    {:else if route === '/upload'}
      <Upload />
    {:else if route === '/files'}
      <FileList />
    {:else if route === '/wallet'}
      {#if WalletComponent}
        <WalletComponent />
      {:else}
        <div class="flex items-center justify-center py-16">
          <div class="w-8 h-8 rounded-full border-2 border-[#21262d] border-t-[#58a6ff] animate-spin"></div>
        </div>
      {/if}
    {:else}
      <Upload />
    {/if}
  </main>
</div>
